import clsx from "clsx";
import React from "react";
import { MenuProps, IconButton } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { MoreHoriz } from "@mui/icons-material";
import { ConfirmActionDialog } from "../dialog/ConfirmActionDialog";
import { useDeleteLibraryExercise } from "./hooks/useDeleteLibraryExercise";
import useArchiveRestoreLibraryExerciseMutation from "./hooks/useArchiveRestoreLibraryExercise";
import {
  BodypartType,
  ExerciseAssetStatus,
  NotificationExerciseBriefDto,
} from "@growth-machine-llc/stridist-api-client";
import { useDirectApprovePendingExerciseLibraryAsset } from "./hooks/useDirectApprovePendingExerciseLibraryAsset";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { TeamRole } from "../../constants";
import { useToggleTeamExerciseVisibilityMutation } from "./hooks/useToggleTeamExerciseVisibility";
import { useExerciseListFilterContext } from "../../contexts/ExerciseLibraryFilterContext";
import { useValidateBodyparts } from "./hooks/useValidateBodypart";
import {
  UserMinus as UnshareIcon,
  Trash as DeleteIcon,
  Share2 as ShareIcon,
  Archive as ArchiveIcon,
  ArchiveRestore as UnarchiveIcon,
  FileCheck as ApproveIcon,
} from "lucide-react";
import { ListMenu, MenuOption } from "../menu/ListMenu";

const useStyles = makeStyles(() => ({
  root: {},
}));

export interface ExercisesMenuProps
  extends Omit<MenuProps, "open" | "onClose"> {
  exerciseId: number;
  archived: boolean;
  pendingApproval?: boolean;
  isTeamShared?: boolean;
  exerciseNotification?: NotificationExerciseBriefDto;
  exerciseCoachId: number;
  bodyparts: BodypartType[];
  onExerciseSelected: () => void;
}

export function ExercisesMenu(props: ExercisesMenuProps) {
  const {
    className,
    exerciseId,
    archived,
    pendingApproval,
    exerciseNotification,
    isTeamShared,
    exerciseCoachId,
    onExerciseSelected,
    bodyparts,
    ...other
  } = props;

  const user = useCurrentUser();

  const validateBodyparts = useValidateBodyparts();

  const { privacy } = useExerciseListFilterContext();

  const shareExerciseEnabled =
    user.isTeamAccount &&
    (user?.teamRole === TeamRole.OWNER || user?.id === exerciseCoachId);

  const isCurrentUserExercise = user?.id === exerciseCoachId;

  const { mutate: deleteExercise } = useDeleteLibraryExercise(
    pendingApproval,
    exerciseNotification,
  );
  const { mutate: restore } = useArchiveRestoreLibraryExerciseMutation(
    ExerciseAssetStatus.ARCHIVED,
    true,
    bodyparts,
  );
  const { mutate: archive } = useArchiveRestoreLibraryExerciseMutation(
    ExerciseAssetStatus.PUBLISHED,
  );

  const { mutate: toggleTeamExerciseVisibility } =
    useToggleTeamExerciseVisibilityMutation(privacy);

  const { mutate: approve } =
    useDirectApprovePendingExerciseLibraryAsset(exerciseNotification);

  const s = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const [confirmExerciseDeleteDialogOpen, setConfirmExerciseDeleteDialogOpen] =
    React.useState(false);

  const handleOpen = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleExerciseDelete = React.useCallback(() => {
    setAnchorEl(null);
    setConfirmExerciseDeleteDialogOpen(true);
  }, [exerciseId, setConfirmExerciseDeleteDialogOpen]);

  const handleExerciseArchive = React.useCallback(() => {
    setAnchorEl(null);
    archive({ id: exerciseId });
  }, [exerciseId, setConfirmExerciseDeleteDialogOpen]);

  const handleConfirmExerciseDelete = React.useCallback(() => {
    deleteExercise(exerciseId);
    setConfirmExerciseDeleteDialogOpen(false);
  }, [exerciseId, deleteExercise]);

  const handleCancelExerciseDelete = React.useCallback(() => {
    setConfirmExerciseDeleteDialogOpen(false);
  }, []);

  const handleExerciseRestore = React.useCallback(() => {
    setAnchorEl(null);
    const isValidBodyparts = validateBodyparts(bodyparts);
    if (!isValidBodyparts) {
      onExerciseSelected();
      return;
    }
    restore({ id: exerciseId });
  }, [exerciseId, restore, validateBodyparts, bodyparts]);

  const handleExerciseApprove = React.useCallback(() => {
    setAnchorEl(null);
    const isValidBodyparts = validateBodyparts(bodyparts);
    if (!isValidBodyparts) {
      onExerciseSelected();
      return;
    }
    approve({ id: exerciseId });
  }, [exerciseId, approve, validateBodyparts, bodyparts]);

  const handleToggleTeamExerciseVisibility = React.useCallback(() => {
    setAnchorEl(null);
    toggleTeamExerciseVisibility({
      id: exerciseId,
      isTeamShared: Boolean(!isTeamShared),
    });
  }, [exerciseId, toggleTeamExerciseVisibility, isTeamShared]);

  if (
    user?.teamRole === TeamRole.OWNER &&
    user?.id !== exerciseCoachId &&
    !isTeamShared
  ) {
    return null;
  }

  const menuOptions: MenuOption[] = [
    {
      label: "Restore",
      onClick: handleExerciseRestore,
      icon: <UnarchiveIcon />,
      visible: archived && isCurrentUserExercise,
    },
    {
      label: "Approve",
      onClick: handleExerciseApprove,
      icon: <ApproveIcon />,
      visible: pendingApproval && isCurrentUserExercise,
    },
    {
      label: "Archive",
      onClick: handleExerciseArchive,
      icon: <ArchiveIcon />,
      visible: !archived && !pendingApproval && isCurrentUserExercise,
    },
    {
      label: "Delete",
      onClick: handleExerciseDelete,
      icon: <DeleteIcon />,
      visible: (archived || pendingApproval) && isCurrentUserExercise,
    },
    {
      label: isTeamShared ? "Remove from Team" : "Share with Team",
      onClick: handleToggleTeamExerciseVisibility,
      icon: isTeamShared ? <UnshareIcon /> : <ShareIcon />,
      visible: !archived && !pendingApproval && shareExerciseEnabled,
    },
  ].filter((item) => item.visible);

  return (
    <>
      <IconButton onClick={handleOpen} size="large">
        <MoreHoriz />
      </IconButton>
      <ListMenu
        open={Boolean(anchorEl)}
        className={clsx(s.root, className)}
        anchorEl={anchorEl}
        options={menuOptions}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      />

      <ConfirmActionDialog
        title="Are you sure you want to delete exercise?"
        open={confirmExerciseDeleteDialogOpen}
        onClose={handleCancelExerciseDelete}
        onCancel={handleCancelExerciseDelete}
        onConfirm={handleConfirmExerciseDelete}
      />
    </>
  );
}
